import { ModalWindow, UIText1, Image } from '@cian/ui-kit';
import { IconActionBlock16, IconFileOpenFolder16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openChatCustomActionModal } from '../../actions/chatCustomActionModal';
import { moveToArchive, moveToSpam } from '../../actions/chatOperations';
import { trackClickMoveToArchive, trackClickMoveToSpam } from '../../actions/userCard/tracking';
import { UserCardMenuContent, EUserCardMenuItemType } from '../../components/UserCardMobile/UserCardMenuContent';
import { UserCardMenuControl } from '../../components/UserCardMobile/UserCardMenuControl';
import { BLOB_ID } from '../../constants/blobId';
import { useCompanionData } from '../../hooks';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { sendCustomActions } from '../../services/sendCustomActions';
import { TCustomAction } from '../../types/customActions';

type TUserCardMenuActionsContainerProps = {
  customActions: TCustomAction[];
};

export const UserCardMenuActionsContainer: React.FC<TUserCardMenuActionsContainerProps> = ({ customActions }) => {
  const dispatch = useDispatch();

  const currentChat = useSelector(currentOpenChatSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const { companionId } = useCompanionData(currentChat?.chatId);

  const [isOpened, setOpened] = React.useState(false);
  const onToggleClick = React.useCallback(() => {
    setOpened(!isOpened);
  }, [isOpened]);

  const onOutside = React.useCallback(() => {
    setOpened(false);
  }, []);

  if (!currentChat?.chatId || currentChat?.chatId === BLOB_ID) {
    return null;
  }

  const items = [];

  const isCanMoveToSpam = currentChat?.availableActions
    ? currentChat.availableActions.indexOf('block') !== -1 && !currentChat.blocked
    : false;

  const isCanMoveToArchive = currentChat?.availableActions
    ? currentChat.availableActions.indexOf('archive') !== -1
    : false;

  if (isCanMoveToArchive) {
    items.push({
      title: (
        <>
          <IconFileOpenFolder16 color="current_color" />
          <UIText1 color="current_color">Перенести в архив</UIText1>
        </>
      ),
      handler: (selectedChatId: string) => {
        setOpened(false);
        dispatch(moveToArchive(selectedChatId));
        trackClickMoveToArchive(currentChat, companionId, opponentId);
      },
      type: EUserCardMenuItemType.Archive,
    });
  }
  if (isCanMoveToSpam) {
    items.push({
      title: (
        <>
          <IconActionBlock16 color="current_color" />
          <UIText1 color="current_color">Заблокировать чат</UIText1>
        </>
      ),
      handler: (selectedChatId: string) => {
        setOpened(false);
        dispatch(moveToSpam(selectedChatId));
        trackClickMoveToSpam(currentChat, companionId, opponentId);
      },
      type: EUserCardMenuItemType.Spam,
    });
  }

  if (customActions) {
    customActions.forEach(button => {
      if (button.action !== 'unsubscribe' && button.action !== 'subscribe') {
        items.push({
          title: (
            <>
              <Image
                height={16}
                width={'auto'}
                borderRadius="0"
                objectFit="contain"
                src={button.desktopIconUrl}
                data-testid="image"
              />
              <UIText1 color="current_color">{button.longText}</UIText1>
            </>
          ),
          handler: (selectedChatId: string) => {
            if (button.alert) {
              dispatch(
                openChatCustomActionModal({
                  text: button.alert.text,
                  cancelButtonText: button.alert.cancelButtonText,
                  confirmButtonText: button.alert.confirmButtonText,
                  action: button.action,
                  chatId: selectedChatId,
                }),
              );

              return;
            }

            dispatch(sendCustomActions({ chatId: selectedChatId, action: button.action, companionId }));
          },
          type: button.action,
        });
      }
    });
  }

  if (!items.length) {
    return null;
  }

  return (
    <div data-testid="UserCardMenuActionsContainer">
      <UserCardMenuControl onClick={onToggleClick} />
      <ModalWindow
        fixed={true}
        open={isOpened}
        onClose={onOutside}
        content={<UserCardMenuContent menuItems={items} currentChatId={currentChat.chatId} />}
      />
    </div>
  );
};
